import { useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { normalizeRole, UserRole } from '@/utils/roles';

export interface AuthUser extends User {
  role?: UserRole;
  schoolId?: string;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const enrichedUser = await enrichUserWithRole(session.user);
        setUser(enrichedUser);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          const enrichedUser = await enrichUserWithRole(session.user);
          setUser(enrichedUser);
        } else {
          setUser(null);
        }
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  return { user, loading };
}

async function enrichUserWithRole(user: User): Promise<AuthUser> {
  try {
    const appMetadata = (user.app_metadata ?? {}) as Record<string, unknown>;
    const userMetadata = (user.user_metadata ?? {}) as Record<string, unknown>;

    // Get user role from user_roles table
    const { data: roleData, error: roleError } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .maybeSingle();

    if (roleError) {
      console.error('Error fetching user role:', roleError);
    }

    // Get user profile for school_id
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('school_id')
      .eq('id', user.id)
      .maybeSingle();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
    }

    const dbRole = normalizeRole(roleData?.role as string | undefined);
    const metadataRole = normalizeRole(
      (appMetadata.app_role as string | undefined) ??
      (userMetadata.app_role as string | undefined) ??
      (userMetadata.role as string | undefined),
    );

    return {
      ...user,
      role: dbRole ?? metadataRole,
      schoolId:
        (profileData?.school_id as string | undefined) ??
        (userMetadata.school_id as string | undefined),
    };
  } catch (error) {
    console.error('Unexpected error enriching user:', error);
    return {
      ...user,
      role: undefined,
      schoolId: undefined,
    };
  }
}

export function useRequireAuth(allowedRoles?: UserRole[]) {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      window.location.href = '/auth';
    }
    if (!loading && user && allowedRoles && !allowedRoles.includes(user.role!)) {
      window.location.href = '/unauthorized';
    }
  }, [user, loading, allowedRoles]);

  return { user, loading };
}
